if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      detectArtworkBwSelection(formData) {
        const normalizeCompact = (value) =>
          String(value || '')
            .toLowerCase()
            .replace(/&amp;|&#38;/g, '&')
            .replace(/[\s\-_/&.]+/g, '');
        const normalizeLoose = (value) => String(value || '').toLowerCase().replace(/&amp;|&#38;/g, '&');
        const variantSelects =
          this.closest('product-info')?.querySelector('variant-selects[data-enable-artwork-bw-media]') ||
          this.querySelector('variant-selects[data-enable-artwork-bw-media]');
        const markerRaw = (variantSelects?.dataset?.artworkBwMarker || 'b&w').trim();
        const markerLoose = normalizeLoose(markerRaw);
        const markerCompact = normalizeCompact(markerRaw);

        const isBwText = (raw) => {
          const loose = normalizeLoose(raw);
          const compact = normalizeCompact(raw);
          if (!loose && !compact) return false;
          if (markerLoose && loose.includes(markerLoose)) return true;
          if (markerCompact && compact.includes(markerCompact)) return true;
          if (compact.includes('blackwhite') || compact.includes('blackandwhite') || compact.includes('bandw')) return true;
          return /\bb\s*[/.&]\s*w\b|\bb\s*&\s*w\b|\bb\s+w\b|\bbw\b|black\s+and\s+white|black\s*&\s*white|grey-?scale|mono(chrome)?|\bmono\b|noir/i.test(
            ` ${loose} `
          );
        };

        const explicitFallback = variantSelects?.querySelector(
          '[data-artwork-bw-fallback] input[data-artwork-bw-fallback-input]:checked'
        );
        if (explicitFallback && isBwText(explicitFallback.value)) return true;

        for (const [key, value] of formData.entries()) {
          if (typeof value !== 'string') continue;
          const keyDown = String(key || '').toLowerCase();
          if (keyDown.includes('properties[__artwork_preview]')) continue;
          if (isBwText(value)) return true;
        }

        return false;
      }

      buildCartAddFormData(includeSections) {
        const fd = new FormData(this.form);
        const isArtworkBw = this.detectArtworkBwSelection(fd);
        fd.set('properties[__artwork_is_bw]', isArtworkBw ? 'true' : 'false');
        if (includeSections && this.cart) {
          fd.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          fd.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        return fd;
      }

      async parseCartAddFetch(response) {
        const ct = response.headers.get('content-type') || '';
        const text = await response.text();
        const trimmed = (text || '').trimStart();
        const looksLikeJson =
          (ct.includes('application/json') ||
            ct.includes('text/json') ||
            ct.includes('text/javascript') ||
            ct.includes('application/javascript')) &&
          /[{[]/.test(trimmed[0] || '');
        const tryParseJsonObject = () => {
          try {
            return JSON.parse(text || '');
          } catch {
            return null;
          }
        };
        /*
          Cart/add often returns HTTP 422 with a JSON body { status, message, description }.
          Shopify sometimes uses Content-Type: text/javascript for the same JSON.
        */
        if (looksLikeJson || (trimmed.startsWith('{') || trimmed.startsWith('['))) {
          const parsed = tryParseJsonObject();
          if (parsed != null) return parsed;
          if (looksLikeJson) {
            const err = new Error('cart-bad-json');
            err.userMessage = "Sorry, we couldn't add this item right now. Please try again.";
            err.context = { status: response.status, contentType: ct, trimmed: (text || '').slice(0, 200), ok: response.ok };
            throw err;
          }
        }
        if (!response.ok) {
          const trimmed = (text || '').slice(0, 200);
          const isRateLimited =
            response.status === 429 ||
            /Verifying your connection|Just a moment|cf-browser-verification/i.test(trimmed);
          const err = new Error(isRateLimited ? 'cart-rate-limited' : 'cart-non-json-response');
          err.userMessage = isRateLimited
            ? "Shopify is rate-limiting cart updates right now. Please wait a moment and try again."
            : "Sorry, we couldn't add this item right now. Please try again.";
          err.context = { status: response.status, contentType: ct, trimmed, ok: response.ok };
          throw err;
        }
        const err = new Error('cart-non-json-response');
        err.userMessage = "Sorry, we couldn't add this item right now. Please try again.";
        err.context = { status: response.status, contentType: ct, trimmed: (text || '').slice(0, 200), ok: response.ok };
        throw err;
      }

      cartAddResponseHasSections(response) {
        if (!response || !response.sections || typeof response.sections !== 'object') return false;
        return this.cart.getSectionsToRender().every((section) => {
          const html = response.sections[section.id];
          return typeof html === 'string' && html.length > 0;
        });
      }

      waitForCartUiTrigger() {
        if (!this.cart || this.cart.tagName !== 'CART-DRAWER') return Promise.resolve();
        if (this.cart.classList.contains('active')) return Promise.resolve();

        return new Promise((resolve) => {
          let settled = false;
          let rafId = null;
          let timeoutId = null;

          const cleanup = () => {
            if (rafId) cancelAnimationFrame(rafId);
            if (timeoutId) clearTimeout(timeoutId);
            this.cart.removeEventListener('cart-drawer:triggered', onTriggered);
          };

          const done = () => {
            if (settled) return;
            settled = true;
            cleanup();
            resolve();
          };

          const onTriggered = () => done();

          const watchActiveClass = () => {
            if (this.cart.classList.contains('active')) {
              done();
              return;
            }
            rafId = requestAnimationFrame(watchActiveClass);
          };

          this.cart.addEventListener('cart-drawer:triggered', onTriggered, { once: true });
          rafId = requestAnimationFrame(watchActiveClass);
          timeoutId = setTimeout(done, 1200);
        });
      }

      async finalizeSuccessfulAdd(response, variantId) {
        const statusRaw = response?.status;
        const statusNum = Number(statusRaw);
        const statusLower = String(statusRaw ?? '').toLowerCase();
        const hasCartLineItems = Array.isArray(response?.items);
        const explicitHttpStyleFailure =
          (Number.isFinite(statusNum) && statusNum >= 400) ||
          statusLower === 'bad_request' ||
          statusLower === 'error';
        const isCartAddError =
          !hasCartLineItems &&
          (explicitHttpStyleFailure || (response?.description && response?.message));
        if (isCartAddError) {
          const msg =
            (response.description && String(response.description).trim()) ||
            (response.message && String(response.message).trim()) ||
            'Could not add this item to the cart.';
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: variantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(msg);

          this.error = true;
          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (soldOutMessage) {
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButtonText.classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
          }
          return;
        }

        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: variantId,
          cartData: response,
        }).then(() => {
          CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
        });
        this.error = false;

        const canRenderSections = this.cartAddResponseHasSections(response);
        const quickAddModal = this.closest('quick-add-modal');

        const paintCartUi = () => {
          if (canRenderSections) {
            this.cart.renderContents(response);
            return;
          }
          if (this.cart.tagName === 'CART-DRAWER' && !document.body.classList.contains('template-cart')) {
            this.cart.open(this.submitButton);
          } else if (this.cart.tagName === 'CART-NOTIFICATION') {
            window.location.assign(window.routes.cart_url);
          }
        };

        if (quickAddModal) {
          const waitForQuickAddClose = new Promise((resolve) => {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  CartPerformance.measure('add:paint-updated-sections', paintCartUi);
                  resolve();
                });
              },
              { once: true }
            );
          });
          quickAddModal.hide(true);
          await waitForQuickAddClose;
        } else {
          CartPerformance.measure('add:paint-updated-sections', paintCartUi);
        }

        await this.waitForCartUiTrigger();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.error = false;
        this.handleErrorMessage();

        this.form = this.querySelector('form');
        if (!this.form) {
          this.error = true;
          this.handleErrorMessage('Add to cart form not found. Refresh the page and try again.');
          return;
        }

        const variantInput = this.form.querySelector('input[name="id"], input.product-variant-id');
        if (variantInput) {
          variantInput.disabled = false;
          variantInput.removeAttribute('disabled');
        }
        if (!variantInput?.value?.trim()) {
          this.error = true;
          this.handleErrorMessage('Choose a valid product option, then add to cart again.');
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const readVariantId = () => this.form.querySelector('[name="id"]')?.value;

        const postAdd = (includeSections) => {
          config.body = this.buildCartAddFormData(includeSections);
          return fetch(`${routes.cart_add_url}`, config).then((r) => this.parseCartAddFetch(r));
        };

        postAdd(Boolean(this.cart))
          .catch((e) => {
            if (e?.message === 'cart-non-json-response' && this.cart && e?.context?.ok !== true) {
              return postAdd(false);
            }
            throw e;
          })
          .then((response) => {
            return this.finalizeSuccessfulAdd(response, readVariantId());
          })
          .catch((e) => {
            console.error('Add to cart failed', e, e?.context);
            this.error = true;
            this.handleErrorMessage(
              e?.userMessage || 'Sorry, we couldn\u2019t add this item right now. Please try again.'
            );
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
